import { motion } from "framer-motion";
import { MoveRight } from "lucide-react";
import { GARMENTS, IMAGES, priceLine } from "@/lib/pricing";
import { scrollToId } from "@/lib/scroll";

const reveal = {
  hidden: { opacity: 0, y: 36 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

const ProductCard = ({ garmentKey, span, height, tag, index, onPick }) => {
  const g = GARMENTS[garmentKey];
  return (
    <motion.article
      variants={reveal}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-80px" }}
      custom={index}
      className={`group relative overflow-hidden rounded-sm border border-[#E5D8D3] bg-white ${span}`}
      data-testid={`product-card-${garmentKey}`}
    >
      <div className={`relative ${height} overflow-hidden`}>
        <img
          src={g.img}
          alt={g.name}
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#141D19]/80 via-[#141D19]/10 to-transparent" />
        {tag && (
          <span className="absolute left-5 top-5 border border-[#D4AF37]/60 bg-[#FDFBF7]/90 px-3 py-1 text-[10px] uppercase tracking-[0.24em] text-[#B08A2E]">
            {tag}
          </span>
        )}
        <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-6 sm:p-8">
          <div>
            <h3 className="ff-serif text-3xl font-light text-[#FDFBF7] sm:text-4xl">{g.name}</h3>
            <p className="mt-1 text-[11px] uppercase tracking-[0.26em] text-[#D4AF37]">{priceLine(garmentKey)}</p>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              onPick(garmentKey);
              scrollToId("#order");
            }}
            className="flex shrink-0 items-center gap-2 rounded-full bg-[#FDFBF7] px-5 py-3 text-[10px] uppercase tracking-[0.22em] text-[#1A2421] transition-colors hover:bg-[#D4AF37]"
            data-testid={`product-order-${garmentKey}`}
          >
            Order <MoveRight size={13} />
          </motion.button>
        </div>
      </div>
    </motion.article>
  );
};

export default function Products({ onPick }) {
  return (
    <section id="collection" className="mx-auto max-w-7xl px-5 py-24 lg:px-10 lg:py-32" data-testid="collection-section">
      <motion.div
        variants={reveal}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-80px" }}
        className="mb-14 flex flex-wrap items-end justify-between gap-6"
      >
        <div>
          <div className="mb-4 flex items-center gap-4">
            <span className="h-px w-12 bg-[#D4AF37]" />
            <span className="text-[11px] uppercase tracking-[0.34em] text-[#2A5C43]">The Collection</span>
          </div>
          <h2 className="ff-serif text-4xl font-light tracking-tight sm:text-5xl">
            Three canvases, <span className="ff-script text-brand-gradient text-5xl font-semibold sm:text-6xl">endless dreams</span>
          </h2>
        </div>
        <p className="max-w-xs text-sm font-light leading-relaxed text-[#1A2421]/60">
          Every piece is a blank canvas — pick your garment, your color, your design, and we craft the rest.
        </p>
      </motion.div>

      <div className="grid gap-6 lg:grid-cols-12">
        <ProductCard garmentKey="tshirt" span="lg:col-span-7" height="h-[420px] lg:h-[540px]" tag="Best Seller" index={0} onPick={onPick} />
        <ProductCard garmentKey="sweatshirt" span="lg:col-span-5" height="h-[420px] lg:h-[540px]" index={1} onPick={onPick} />
        <ProductCard garmentKey="hoodie" span="lg:col-span-5" height="h-[380px]" index={2} onPick={onPick} />

        <motion.article
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          custom={3}
          className="group relative flex flex-col overflow-hidden rounded-sm border border-[#E5D8D3] bg-[#16211C] sm:flex-row lg:col-span-7"
          data-testid="craft-card"
        >
          <div className="relative h-56 overflow-hidden sm:h-auto sm:w-2/5">
            <img
              src={IMAGES.craft}
              alt="Hand-finishing detail on a custom garment"
              className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
            />
          </div>
          <div className="flex flex-1 flex-col justify-center p-8 sm:p-10">
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">The Craft</span>
            <h3 className="ff-serif mt-3 text-3xl font-light text-[#FDFBF7]">
              Made by hand, <span className="italic text-[#D87D92]">made for you</span>
            </h3>
            <p className="mt-4 text-sm font-light leading-relaxed text-[#FDFBF7]/65">
              Each creation is pressed, finished, and checked in-studio before it ships. Add $10 flat
              shipping to any order — one box, straight to your door.
            </p>
            <button
              onClick={() => scrollToId("#order")}
              className="mt-6 flex w-fit items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-[#D4AF37] transition-colors hover:text-[#FDFBF7]"
              data-testid="craft-order-link"
            >
              Begin yours <MoveRight size={13} />
            </button>
          </div>
        </motion.article>
      </div>
    </section>
  );
}
